import React, { useCallback, useEffect, useRef } from 'react';
import { type Maybe, type SaleCalendar } from '@gql/schema';
import { InlineDateRangePicker, css } from '@secretescapes/falcon';
import { useApplicationState } from '@utils/useApplicationState';
import { BullfinchFeatureFlags, useFeatureFlag } from '@utils/useFeatureFlag';
import moment from 'moment';
import 'react-dates/initialize';
import { type TFocusedInput } from '../AvailabilityPresentation.interfaces';
import { CALENDAR_DAY_DIMENSIONS_PX, getLastDatesToShow } from '../AvailabilityPresentation.utils';
import {
  type CalendarDates,
  type NullableMoment,
  type RenderMonthElement,
  type VisibleMonths,
} from './Calendar.interfaces';
import {
  getAdditionalCalendarProps,
  getCheckOutEntry,
  getDayData,
  getVisibleMonths,
  navigateNextMonth,
} from './Calendar.utils';
import { CalendarDay } from './CalendarDay';
import { CalendarMonth } from './CalendarMonth';

interface Props {
  onDatesChange: ({ startDate, endDate }: CalendarDates) => void;
  onFocusChange: (arg: TFocusedInput) => void;
  startDate: NullableMoment;
  endDate: NullableMoment;
  focusedInput: TFocusedInput;
  availability: Maybe<SaleCalendar>;
  availabilityLoading: boolean;
  withFlights: boolean;
  rooms: number;
}

export const Calendar = ({
  onDatesChange,
  onFocusChange,
  startDate,
  endDate,
  focusedInput,
  availability,
  availabilityLoading,
  withFlights,
  rooms,
}: Props) => {
  const { currency, isMobile, territoryLocale } = useApplicationState();
  const bookingFeesEnabled = useFeatureFlag(BullfinchFeatureFlags.bookingFeesEnabled);

  const visibleMonths = useRef<VisibleMonths>({});

  const availableCheckInDates = availability?.checkInDates;

  const lastCheckOutDate = availability?.lastAvailableCheckOutDate;
  const firstDatesToShow = availability?.firstAvailableCheckInDate
    ? moment(availability?.firstAvailableCheckInDate)
    : moment();
  const lastDatesToShow = getLastDatesToShow(availability, lastCheckOutDate);
  const startDateMonth = startDate?.format('MMMM Y');

  // All values undefined if not mobile
  const { key, initialVisibleMonth, numberOfMonths } = getAdditionalCalendarProps(
    !!isMobile,
    firstDatesToShow,
    lastDatesToShow,
    territoryLocale
  );

  useEffect(() => {
    if (isMobile) {
      // Get selected start date month
      const selectedMonth = document.getElementById(`month-${startDateMonth}`);

      // if selected month is in bottom half of screen scroll to top
      if (selectedMonth && selectedMonth.getBoundingClientRect().top > window.innerHeight / 2) {
        selectedMonth.scrollIntoView(true);
      }
    }
  }, [isMobile, startDateMonth]);

  const handleDatesChange = ({ startDate: newStartDate, endDate: newEndDate }: CalendarDates) => {
    if (newStartDate?.isSame(startDate, 'd')) {
      onDatesChange({ startDate: newStartDate, endDate: newEndDate });

      return;
    }

    if (newStartDate) {
      const checkOutEntry = getCheckOutEntry(newStartDate, endDate, availableCheckInDates);

      // If existing end date doesn't have check out select first check out of new check in
      const updatedEndDate = checkOutEntry?.date
        ? moment(checkOutEntry.date)
        : moment(getCheckOutEntry(newStartDate, null, availableCheckInDates)?.date);

      onDatesChange({ startDate: newStartDate, endDate: updatedEndDate });

      if (!isMobile && updatedEndDate.isAfter(visibleMonths.current.last, 'month')) {
        navigateNextMonth();
      }
    }
  };

  const renderDay = (day: moment.Moment, daySize?: number) => {
    const dayData = getDayData(day, startDate, endDate, availableCheckInDates, {
      focusedInput,
      currency,
      territory: territoryLocale,
      withFlights,
      rooms,
      applyBookingFees: bookingFeesEnabled,
    });

    return (
      <CalendarDay
        data={dayData}
        startDate={startDate}
        endDate={endDate}
        daySize={daySize}
        isLoading={availabilityLoading}
        isMobile={!!isMobile}
      />
    );
  };

  const isDayBlocked = (day: moment.Moment) => {
    const { isAvailable } = getDayData(day, startDate, endDate, availableCheckInDates, {
      focusedInput,
      currency,
      territory: territoryLocale,
    });

    return !isAvailable;
  };

  const renderMonthElement = ({ isVisible, month }: RenderMonthElement) => {
    // Update visible months ref
    if (isVisible) {
      visibleMonths.current = getVisibleMonths(visibleMonths.current, month);
    }

    const hasAvails = availableCheckInDates?.some(calendarItem =>
      moment(calendarItem?.date).isSame(month, 'month')
    );

    const showNoAvailabilityBadge =
      !!availability?.checkInDates.length &&
      !hasAvails &&
      !month.isAfter(moment(availability?.lastAvailableCheckInDate), 'month') &&
      !availabilityLoading;

    return (
      <CalendarMonth
        month={month.locale(territoryLocale)}
        showNoAvailabilityBadge={showNoAvailabilityBadge}
        isMobile={!!isMobile}
      />
    );
  };

  const performanceCalendarRenderEnd = useCallback(() => {
    if (performance.getEntriesByName('start-render')?.length > 0) {
      performance.mark('end-render');
      performance.measure('calendar-render-time', 'start-render', 'end-render');
      performance.clearMarks();
    }
  }, []);

  useEffect(() => {
    if (!availabilityLoading && availability) {
      performanceCalendarRenderEnd();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availability, performanceCalendarRenderEnd]);

  return (
    <InlineDateRangePicker
      key={key}
      dataAttributes={{ testid: 'availability-calendar' }}
      startDate={startDate}
      endDate={endDate}
      minDate={firstDatesToShow.locale(territoryLocale)}
      maxDate={availability ? lastDatesToShow.locale(territoryLocale) : undefined}
      focusedInput={focusedInput}
      locale={territoryLocale}
      noBorder={true}
      renderDayContents={renderDay}
      daySize={CALENDAR_DAY_DIMENSIONS_PX}
      isDayBlocked={isDayBlocked}
      renderMonthElement={renderMonthElement}
      onDatesChange={handleDatesChange}
      onFocusChange={onFocusChange}
      fitsContainerWidth={isMobile}
      initialVisibleMonth={initialVisibleMonth}
      numberOfMonths={numberOfMonths}
      calendarScrollerStyles={css`
        padding-bottom: 350px;
      `}
    />
  );
};
